import {
  getDataCenterDataById,
  useGetTableDataInfinite,
} from "@/queries/data-center/data-center.api";
import { cn } from "@/utils/theme";
import { useDebounceHook } from "@/hooks/use-debounce";
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@elaniintc/goes-design-system";
import { useState, useEffect, useMemo, useRef } from "react";

type OptionItem = { key: string; label: string; table?: string };

async function fetchMissingOptionItems(
  missingIds: string[],
  tableName: string,
  columnName: string,
): Promise<OptionItem[]> {
  const fetched: OptionItem[] = [];
  for (const id of missingIds) {
    try {
      const response = await getDataCenterDataById(tableName, id);
      if (response?.data?.length > 0) {
        const item = response.data[0];
        fetched.push({
          key: item.id,
          label: item[columnName],
          table: tableName,
        });
      }
    } catch (error) {
      console.error(`Error fetching option ${id}:`, error);
    }
  }
  return fetched;
}

function mergeUniqueOptions(
  existing: OptionItem[],
  newItems: OptionItem[],
): OptionItem[] {
  const existingKeys = new Set(existing.map((opt) => opt.key));
  const unique = newItems.filter((opt) => !existingKeys.has(opt.key));
  return [...unique, ...existing];
}

function resolveOptionsByIds(
  ids: string[],
  allOptions: OptionItem[],
): OptionItem[] {
  return ids
    .map((id) => allOptions.find((opt) => opt.key === id))
    .filter((opt): opt is OptionItem => opt !== undefined);
}

interface DataCenterDataMultiselectProps {
  disabled?: boolean;
  placeholder?: string;
  initOptions?: string[];
  value?: Array<{ key: string; label: string; table?: string }>;
  onChange?: (
    value: Array<{ key: string; label: string; table?: string }>,
  ) => void;
  onBlur?: () => void;
  group?: "left" | "right" | "single" | "down-left" | "down-right";
  database?: {
    table: string | null | undefined;
    column: string | null | undefined;
  };
  className?: string;
  wrapperClassName?: string;
  enableSearch?: boolean;
}

export const DataCenterDataMultiselect = ({
  disabled,
  placeholder = "Selecciona una opción",
  initOptions,
  value,
  onChange,
  onBlur,
  group,
  database,
  wrapperClassName,
  className,
  enableSearch,
}: DataCenterDataMultiselectProps) => {
  const [options, setOptions] = useState<
    { key: string; label: string; table?: string }[]
  >(
    initOptions
      ?.filter(Boolean)
      .map((option) => ({ key: option, label: option })) || [],
  );
  const [searchString, setSearchString] = useState("");
  const debouncedSearch = useDebounceHook(searchString, 300);
  const lastProcessedValueRef = useRef<string>("");

  // Use infinite query for database-driven selects
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingInfinite,
  } = useGetTableDataInfinite({
    name: database?.table || "",
    search: debouncedSearch,
    per_page: 15,
  });

  // Transform infinite query data to options
  const databaseOptions = useMemo(() => {
    if (!database?.table || !database?.column || !infiniteData) return [];

    const column_name = database.column;
    const table_name = database.table;

    return infiniteData.pages.flatMap((page) =>
      (page.data?.data || []).map(
        (item: { [key: string]: string | number }) => ({
          key: item.id as string,
          label: item[column_name] as string,
          table: table_name,
        }),
      ),
    );
  }, [infiniteData, database?.table, database?.column]);

  // Set options based on database or initial options
  useEffect(() => {
    if (database?.table) {
      setOptions(databaseOptions);
    } else if (initOptions && !database?.table) {
      setOptions(
        initOptions.filter(Boolean).map((option) => ({
          key: option,
          label: option,
        })),
      );
    }
  }, [databaseOptions, initOptions, database?.table]);

  useEffect(() => {
    if (!onChange || !database?.table || !database?.column) return;
    if (!value || !Array.isArray(value) || options.length === 0) return;

    const currentValueStr = JSON.stringify(value);
    if (lastProcessedValueRef.current === currentValueStr) return;

    const hasStringValues = value.every((v) => typeof v === "string");

    if (!hasStringValues) {
      const needsNormalization =
        value.length > 0 && value[0].key === value[0].label;
      if (!needsNormalization) return;

      const normalizedValue = resolveOptionsByIds(
        value.map((v) => (typeof v === "string" ? v : v.key)),
        options,
      );
      const normalizedStr = JSON.stringify(normalizedValue);
      if (normalizedStr !== currentValueStr) {
        lastProcessedValueRef.current = normalizedStr;
        onChange(normalizedValue);
      }
      return;
    }

    const stringIds = value as unknown as string[];
    const optionsFound = options.filter((opt) => stringIds.includes(opt.key));

    if (optionsFound.length === value.length) {
      const foundStr = JSON.stringify(optionsFound);
      if (foundStr !== currentValueStr) {
        lastProcessedValueRef.current = foundStr;
        onChange(optionsFound);
      }
      return;
    }

    const missingIds = stringIds.filter(
      (id) => !options.some((opt) => opt.key === id),
    );
    if (missingIds.length === 0) return;

    const handleMissing = async () => {
      try {
        const fetched = await fetchMissingOptionItems(
          missingIds,
          database.table!,
          database.column!,
        );
        if (fetched.length > 0) {
          const allOptions = mergeUniqueOptions(options, fetched);
          const newValue = resolveOptionsByIds(stringIds, allOptions);
          const newValueStr = JSON.stringify(newValue);
          if (newValueStr !== currentValueStr) {
            lastProcessedValueRef.current = newValueStr;
            onChange(newValue);
          }
          setOptions(allOptions);
        }
      } catch (error) {
        console.error("Error fetching missing options:", error);
      }
    };

    handleMissing();
  }, [options, value, onChange, database?.table, database?.column]);

  // Filter options locally when not using database
  const filteredOptions = useMemo(() => {
    // For database-driven selects, options are already filtered by API
    if (database?.table) {
      return options;
    }
    // For local options, filter client-side
    if (!searchString.trim()) {
      return options;
    }
    const search = searchString.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(search));
  }, [options, searchString, database?.table]);

  const SearchBox = enableSearch ? (
    <div className="border-b border-neutral-quiet p-3 relative ">
      <Icon
        type="far"
        name="search"
        className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
      />
      <Input
        variant="sm"
        className="text-sm w-full font-normal flex-auto pl-9"
        placeholder={"Buscar..."}
        onChange={(e) => setSearchString(e.target.value)}
        value={searchString}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  ) : null;

  return (
    <div className={cn("min-w-0 flex-1 h-full ", wrapperClassName)}>
      <Popover onOpenChange={(open) => !open && onBlur?.()}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            group={group}
            variant="tertiary"
            className={cn(
              "w-full h-full justify-between group items-center min-h-10 px-2 relative",
              "text-body-quiet leading-3xl font-normal",
              className,
            )}
            disabled={disabled}
          >
            {isLoadingInfinite ? (
              <span>Cargando...</span>
            ) : (
              <span className="truncate pr-6">
                {value && Array.isArray(value) && value.length > 0
                  ? value
                      .map((v) => (typeof v === "object" ? v.label : v))
                      .join(", ")
                  : placeholder}
              </span>
            )}
            {isLoadingInfinite ? (
              <Icon
                name="spinner-third"
                type="fas"
                className="ml-auto shrink-0 animate-spin"
              />
            ) : (
              <Icon
                type="far"
                name="angle-down"
                className="shrink-0 group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
              />
            )}
          </Button>
        </PopoverTrigger>
        {!disabled && !isLoadingInfinite && (
          <PopoverContent className="p-2 shadow-md w-(--radix-popover-trigger-width)">
            {SearchBox}
            <div className="max-h-48 overflow-y-auto flex flex-col gap-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <label
                    key={option.key}
                    className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-body-quiet"
                  >
                    <Checkbox
                      checked={value?.some(
                        (v) =>
                          (typeof v === "string"
                            ? v
                            : (v as { key: string; label: string }).key) ===
                          option.key,
                      )}
                      onChange={() => {
                        let newValue: Array<{ key: string; label: string }>;
                        const isSelected = value?.some(
                          (v) =>
                            (typeof v === "string"
                              ? v
                              : (v as { key: string; label: string }).key) ===
                            option.key,
                        );
                        if (isSelected) {
                          newValue =
                            value?.filter(
                              (v) =>
                                (typeof v === "string"
                                  ? v
                                  : (v as { key: string; label: string })
                                      .key) !== option.key,
                            ) || [];
                        } else {
                          newValue = [...(value ?? []), option];
                        }
                        onChange?.(newValue);
                      }}
                      disabled={disabled}
                    />
                    {option.label}
                  </label>
                ))
              ) : (
                <div className="flex items-center justify-center min-h-40 px-4">
                  <p className="text-body-quiet text-base font-normal text-center">
                    {searchString.trim().length > 0
                      ? "No se encontraron resultados"
                      : "No hay opciones disponibles"}
                  </p>
                </div>
              )}
              {hasNextPage && (
                <div className="w-full mt-2">
                  <Button
                    variant="ghost-blue"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Icon
                          name="spinner-third"
                          type="fas"
                          className="animate-spin mr-2"
                        />
                        Cargando...
                      </>
                    ) : (
                      <>
                        Cargar más
                        <Icon name="add" type="fas" className="ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
};
